import { useEffect, useRef } from 'react';
import anime from 'animejs';
import styled from '@emotion/styled';

const Test = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const rtlRef = useRef<HTMLDivElement>(null);
  const ltrRef = useRef<HTMLDivElement>(null);
  const scrollAnimeRef = useRef<HTMLDivElement>(null);
  const ioRef = useRef<HTMLDivElement>(null);

  const scrollPercent = () => {
    const $div = scrollAnimeRef.current;
    const divRect = $div.getBoundingClientRect();

    if (divRect.top <= 0) {
      return (Math.abs(divRect.top) / divRect.height) * 100;
    } else {
      return 0;
    }
  };

  const animateImage = () => {
    anime({
      targets: '#parts_face',
      translateX: '+= 300',
      translateY: '+= 200',
      easing: 'easeInOutSine',
      duration: 700,
    });

    anime({
      targets: '#parts_clothes',
      translateX: '-= 300',
      translateY: '+= 100',
      easing: 'easeInOutSine',
      duration: 700,
    });

    anime({
      targets: '#parts_friend_01',
      translateX: '+= 240',
      translateY: '-= 100',
      easing: 'easeInOutSine',
      duration: 700,
    });

    anime({
      targets: '#parts_friend_03',
      translateX: '-= 200',
      translateY: '-= 200',
      easing: 'easeInOutSine',
      duration: 700,
    }).finished.then(() => {
      // finished.then은 위 애니메이션이 끝나고, 새로운 애니메이션을 시작함. (별도의 loop)
      // timeline은 위 애니메이션이 끝나고, 이어서 애니메이션을 시작함. (한 loop)
      anime({
        targets: '.scale',
        easing: 'easeInOutSine',
        duration: 1000,
        // scale: 1.1,
        translateY: '-= 10',
        direction: 'alternate', // (0% -> 100% -> 0%)
        loop: true,
      });
    });
  };

  const animateImageWithIO = (
    [entry]: any,
    observer: { unobserve: (arg0: any) => void },
  ) => {
    if (entry.intersectionRatio > 0) {
      animateImage();
      observer.unobserve(entry.target);
    }
  };

  // intersection observer
  useEffect(() => {
    const observer = new IntersectionObserver(animateImageWithIO, {
      threshold: 0.3,
    });

    observer.observe(ioRef.current);

    return () => {
      observer && observer.disconnect();
    };
  }, []);

  // Scroll RTL, LTR Animation
  useEffect(() => {
    const rtlAnimation = anime({
      targets: '.right',
      translateX: ['100%', 0],
      easing: 'linear',
      autoplay: false,
    });

    const ltrAnimation = anime({
      targets: '.left',
      translateX: [-50, '100%'],
      easing: 'linear',
      autoplay: false,
    });

    window.addEventListener('scroll', () => {
      rtlAnimation.seek((scrollPercent() / 100) * rtlAnimation.duration);
      ltrAnimation.seek((scrollPercent() / 100) * ltrAnimation.duration);
    });
  }, []);

  // background
  const starryNight = () => {
    anime({
      targets: ['#sky .star'],
      opacity: [
        {
          duration: 300,
          value: '0',
        },
        {
          duration: 300,
          value: '1',
        },
      ],
      easing: 'linear',
      loop: true,
      delay: (el, i) => 50 * i,
    });
  };

  // const shootingStars = () => {
  //   anime({
  //     targets: ['#shootingstars .wish'],
  //     easing: 'linear',
  //     loop: true,
  //     delay: (el, i) => 500 * i,
  //     opacity: [
  //       {
  //         duration: 700,
  //         value: '1',
  //       },
  //     ],
  //     width: [
  //       {
  //         value: '150px',
  //       },
  //       {
  //         value: '0px',
  //       },
  //     ],
  //     translateX: 350,
  //   });
  // };

  const randomRadius = () => {
    return Math.random() * 0.8 + 0.6;
  };
  const getRandomX = (vw) => {
    return Math.floor(Math.random() * Math.floor(vw)).toString();
  };
  const getRandomY = (vh) => {
    return Math.floor(Math.random() * Math.floor(vh)).toString();
  };

  useEffect(() => {
    starryNight();
  }, []);

  return (
    <Container ref={containerRef}>
      <Sky id='sky'>
        {Array(500)
          .fill(0)
          .map((_, i) => {
            return (
              <Star
                cx={getRandomX(2000)}
                cy={getRandomY(2000)}
                r={randomRadius()}
                stroke='none'
                strokeWidth={0}
                fill='white'
                key={String(i)}
                className='star'
              />
            );
          })}
      </Sky>

      <div
        style={{
          height: 1000,
          border: '1px solid #eee',
        }}
      >
        Go Down
      </div>
      <div
        ref={scrollAnimeRef}
        style={{
          height: 1000,
          border: '1px solid red',
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
        }}
      >
        <h1
          ref={rtlRef}
          className='right'
          style={{
            fontSize: 96,
            textTransform: 'uppercase',
            alignSelf: 'flex-end',
          }}
        >
          Right Side Text
        </h1>
        <h1
          ref={ltrRef}
          className='left'
          style={{
            fontSize: 96,
            textTransform: 'uppercase',
            alignSelf: 'flex-start',
          }}
        >
          Left Side Text
        </h1>
      </div>
      <div
        ref={ioRef}
        style={{
          height: 1000,
          border: '1px solid blue',
        }}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <AbsoluteImg alt='alt' id='parts_bg' src='/bg_01.png' />
          <AbsoluteImg alt='alt' id='parts_body' src='/body.png' />
          <AbsoluteImg alt='alt' id='parts_head' src='/head_01.png' />
          <AbsoluteImg
            alt='alt'
            className='scale'
            id='parts_face'
            src='/face_01.png'
          />
          <AbsoluteImg
            alt='alt'
            className='scale'
            id='parts_clothes'
            src='/clothes_1.png'
          />
          <AbsoluteImg
            alt='alt'
            className='scale'
            id='parts_friend_01'
            src='/friend_01.png'
          />
          <AbsoluteImg
            alt='alt'
            className='scale'
            id='parts_friend_03'
            src='/friend_03.png'
          />
        </div>
      </div>
    </Container>
  );
};

const Container = styled.div`
  background-color: #000000;
  color: #ffffff;
  overflow: hidden;
`;

const AbsoluteImg = styled.img`
  position: absolute;
  top: 20%;
`;

const Sky = styled.svg`
  width: 100vw;
  height: 100vh;
  position: fixed;
  overflow: hidden;
  margin: 0;
  padding: 0;
`;
const Star = styled.circle``;

export default Test;
